import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../../contexts/CompanyContext';
import { usePlanModal } from '../../providers';
import { useAgent } from '../../contexts/AgentContext';
import './trial-banner.scss';

const GiftIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <g clipPath='url(#clip0_trial_banner)'>
      <path
        d='M10.0003 4.99935V18.3327M10.0003 4.99935H7.0539C6.61976 4.99935 6.20341 4.82375 5.89643 4.51119C5.58945 4.19863 5.41699 3.77471 5.41699 3.33268C5.41699 2.89065 5.58945 2.46673 5.89643 2.15417C6.20341 1.84161 6.61976 1.66602 7.0539 1.66602C9.34556 1.66602 10.0003 4.99935 10.0003 4.99935ZM10.0003 4.99935H12.9468C13.3809 4.99935 13.7972 4.82375 14.1042 4.51119C14.4112 4.19863 14.5837 3.77471 14.5837 3.33268C14.5837 2.89065 14.4112 2.46673 14.1042 2.15417C13.7972 1.84161 13.3809 1.66602 12.9468 1.66602C10.6551 1.66602 10.0003 4.99935 10.0003 4.99935ZM16.667 9.16602V15.666C16.667 16.5994 16.667 17.0661 16.4853 17.4227C16.3255 17.7363 16.0706 17.9912 15.757 18.151C15.4005 18.3327 14.9337 18.3327 14.0003 18.3327L6.00033 18.3327C5.0669 18.3327 4.60019 18.3327 4.24367 18.151C3.93007 17.9912 3.6751 17.7363 3.51531 17.4227C3.33366 17.0661 3.33366 16.5994 3.33366 15.666V9.16602M1.66699 6.33268L1.66699 7.83268C1.66699 8.29939 1.66699 8.53275 1.75782 8.71101C1.83771 8.86781 1.9652 8.99529 2.122 9.07519C2.30026 9.16601 2.53362 9.16601 3.00033 9.16601L17.0003 9.16602C17.467 9.16602 17.7004 9.16602 17.8787 9.07519C18.0355 8.99529 18.1629 8.86781 18.2428 8.71101C18.3337 8.53275 18.3337 8.29939 18.3337 7.83268V6.33268C18.3337 5.86597 18.3337 5.63262 18.2428 5.45436C18.1629 5.29756 18.0355 5.17007 17.8787 5.09018C17.7004 4.99935 17.467 4.99935 17.0003 4.99935L3.00033 4.99935C2.53362 4.99935 2.30026 4.99935 2.122 5.09018C1.9652 5.17007 1.83771 5.29756 1.75782 5.45436C1.66699 5.63262 1.66699 5.86597 1.66699 6.33268Z'
        stroke='#489FD9'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </g>
    <defs>
      <clipPath id='clip0_trial_banner'>
        <rect width='20' height='20' fill='white' />
      </clipPath>
    </defs>
  </svg>
);

const InfoIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <g clipPath='url(#clip0_info_banner)'>
      <path
        d='M10.0003 13.3346V10.0013M10.0003 6.66797H10.0087M18.3337 10.0013C18.3337 14.6037 14.6027 18.3346 10.0003 18.3346C5.39795 18.3346 1.66699 14.6037 1.66699 10.0013C1.66699 5.39893 5.39795 1.66797 10.0003 1.66797C14.6027 1.66797 18.3337 5.39893 18.3337 10.0013Z'
        stroke='#838B9B'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </g>
    <defs>
      <clipPath id='clip0_info_banner'>
        <rect width='20' height='20' fill='white' />
      </clipPath>
    </defs>
  </svg>
);

function formatResetDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

const TrialBanner: FC = () => {
  const {
    hasSubscription,
    aiTimeMinutes,
    resetsAt,
    hasPersonalAssistant,
    isOpsInternalUser,
  } = useCompany();
  const { openPlanModal } = usePlanModal();
  const navigate = useNavigate();
  const { agentMode } = useAgent();

  // Never show for ops internal users (human-task workers)
  if (isOpsInternalUser) return null;

  // Only show for PA users when PA is the active mode
  if (!hasPersonalAssistant || agentMode !== 'personal') return null;

  // Determine which variant to show
  const LOW_AI_THRESHOLD = 60; // 1 hour in minutes

  if (!hasSubscription) {
    // Variant 1: Trial / no subscription
    return (
      <div className='trial-banner'>
        <div className='trial-banner__content'>
          <GiftIcon />
          <p className='trial-banner__text'>
            <span className='trial-banner__text--bold'>
              You're using Dream in trial mode.{' '}
            </span>
            You have 1 hour of AI time. To access human tasks,{' '}
            <button
              className='trial-banner__link'
              onClick={() => openPlanModal()}
            >
              choose a plan
            </button>
          </p>
        </div>
        <button
          className='trial-banner__button'
          onClick={() => openPlanModal()}
        >
          Choose your plan
        </button>
      </div>
    );
  }

  if (aiTimeMinutes <= 0) {
    // Variant 3: Subscribed, out of AI time
    const resetLabel = resetsAt
      ? `wait until ${formatResetDate(resetsAt)} for more time`
      : 'wait until your plan renews for more time';

    return (
      <div className='trial-banner'>
        <div className='trial-banner__content'>
          <InfoIcon />
          <p className='trial-banner__text'>
            <span className='trial-banner__text--bold'>
              You're out of AI time.{' '}
            </span>
            Top up, or {resetLabel}.
          </p>
        </div>
        <button
          className='trial-banner__button'
          onClick={() => navigate('/settings')}
        >
          Add a top-up
        </button>
      </div>
    );
  }

  if (aiTimeMinutes < LOW_AI_THRESHOLD) {
    // Variant 2: Subscribed, less than 1h remaining
    return (
      <div className='trial-banner'>
        <div className='trial-banner__content'>
          <InfoIcon />
          <p className='trial-banner__text'>
            <span className='trial-banner__text--bold'>
              You have less than 1 hour of AI time left.{' '}
            </span>
            Top up to keep Projects and Autopilots running.
          </p>
        </div>
        <button
          className='trial-banner__button'
          onClick={() => navigate('/settings')}
        >
          Add a top-up
        </button>
      </div>
    );
  }

  // Subscribed with enough AI time — no banner
  return null;
};

export default TrialBanner;
